// Logic hiển thị CỔNG AI review (260928 E3-P2 H11) — hàm thuần, KHÔNG import antd (antd v6 không load được dưới jest của react-scripts).
// Khối `AdaptationGateCard.tsx` chỉ vẽ lại kết quả của `gateView`; mọi quyết định (màu · nhãn · nút nào bật) nằm ở đây để test được.
import type { GateIssue, GateStepKey, ReviewGates, StepGate } from '../../types/adaptation';
import { redactMoney } from './workbench/moneyRedact';

/** Giữ khớp `GATE_COST_CAP_USD` bên admin-service (`utils/reviewGate.ts`) — đổi bên nào thì đổi cả hai. */
export const GATE_COST_CAP_USD = 5;
/** Giữ khớp `OVERRIDE_REASON_MIN` bên admin-service. */
export const OVERRIDE_REASON_MIN = 5;

export type GateViewState = 'NONE' | 'PASSED' | 'BLOCKED' | 'OVERRIDDEN' | 'FAILED' | 'SKIPPED_BUDGET' | 'SKIPPED_OFF' | 'STALE';
export type GateColor = 'default' | 'success' | 'error' | 'warning' | 'info';

export interface GateIssueView {
  id: string;
  /** "Tập 4" · "Rowan Ashcroft (char_rowan)" · "Bảng tên". */
  where: string;
  dimension: string;
  dimensionLabel: string;
  problem: string;
  quote: string;
  suggestedFix: string;
}

export interface GateView {
  state: GateViewState;
  color: GateColor;
  label: string;
  description: string;
  blocking: GateIssueView[];
  advisory: GateIssueView[];
  /** Cổng đang CHẶN bước kế (BLOCKED và không stale). */
  blocksNext: boolean;
  /** "Sửa lại" = chạy lại BƯỚC (không phải chấm lại). */
  canRerunStep: boolean;
  /** "Chấm lại" = chỉ chấm lại trạng thái hiện tại (sau khi sửa tay). */
  canRejudge: boolean;
  /** "Vẫn tiếp tục" = vượt cổng kèm lý do. */
  canOverride: boolean;
  rerunHint: string;
  overrideInfo?: string;
  notes: string[];
}

const DIMENSION_LABEL: Record<string, string> = {
  no_fabrication: 'Bịa tình tiết',
  source_coverage: 'Bỏ sót nguyên tác',
  source_name_leak: 'Lộ tên gốc',
  // 260928 E3-P3 — người đọc AI cả mùa.
  CHARACTER_STATE_CONTRADICTION: 'Mâu thuẫn trạng thái nhân vật',
  KNOWLEDGE_LEAK: 'Nhân vật biết điều chưa được kể',
  UNRESOLVED_CLIFFHANGER_DROPPED: 'Câu treo bị bỏ quên',
  CHARACTER_TELEPORTATION: 'Nhân vật dịch chuyển không lý do',
  OTHER: 'Ghi chú khác',
};

/** Nhãn tiếng Việt của một chiều/loại lỗi; lạ ⇒ giữ nguyên mã. */
export const dimensionLabelOf = (dimension: string): string => DIMENSION_LABEL[dimension] ?? dimension;

/** Audit luôn đọc theo giờ Việt Nam; ID nội bộ không đi ra câu cho người dùng. */
export const overrideActorText = (override: StepGate['override']): string => override?.displayBy?.trim() || 'một quản trị viên';
export const formatVnAuditTime = (value: string | null | undefined): string => {
  const d = new Date(String(value ?? ''));
  if (Number.isNaN(d.getTime())) return '';
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Ho_Chi_Minh', hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric', hour12: false }).formatToParts(d);
  const part = (type: string) => p.find((x) => x.type === type)?.value ?? '';
  return `${part('hour')}:${part('minute')} ${part('day')}/${part('month')}/${part('year')}`;
};

/** Tổng chi cộng dồn của truyện: `spentUsd` nếu có (không reset khi chạy lại bước), không thì cộng `costUsd` các cổng. */
export const gateSpentUsd = (gates: ReviewGates | null | undefined): number => {
  if (!gates) return 0;
  if (typeof gates.spentUsd === 'number' && Number.isFinite(gates.spentUsd)) return gates.spentUsd;
  return [gates.localize, gates.episode_split, gates.season_reader].reduce((n, g) => n + (g && Number.isFinite(g.costUsd) ? g.costUsd : 0), 0);
};

export const isOverrideReasonValid = (reason: string | null | undefined): boolean =>
  typeof reason === 'string' && reason.trim().length >= OVERRIDE_REASON_MIN;

const ENTITY_LINE = /^(\S+) · (.+?) \(/;

/** Lỗi nằm ở đâu: `ep:N` ⇒ "Tập N"; `cast` ⇒ thực thể lấy từ dòng `id · name (…)` mà giám khảo trích; không có ⇒ "Bảng tên". */
export const issueWhere = (issue: Pick<GateIssue, 'unit' | 'evidence'>): string => {
  const ep = /^ep:(\d+)$/.exec(issue.unit ?? '');
  if (ep) return `Tập ${ep[1]}`;
  for (const e of issue.evidence ?? []) {
    const m = ENTITY_LINE.exec(String(e?.quote ?? '').trimStart().split(/\r?\n/)[0] ?? '');
    if (m) return `${m[2]} (${m[1]})`;
  }
  return issue.unit === 'cast' ? 'Bảng tên' : String(issue.unit ?? '');
};

const toIssueView = (i: GateIssue): GateIssueView => {
  const ev = (i.evidence ?? []).find((e) => e.valid) ?? (i.evidence ?? [])[0];
  return {
    id: i.id, where: issueWhere(i), dimension: i.dimension, dimensionLabel: dimensionLabelOf(i.dimension),
    problem: i.problem, quote: String(ev?.quote ?? '').slice(0, 220), suggestedFix: i.suggestedFix,
  };
};

/** 261015 P3a — lý do tập lỗi KHÔNG được tự sửa, đọc được (khớp BE `AutoFixUnresolvedReason`). */
export const AUTOFIX_UNRESOLVED_LABEL: Record<string, string> = {
  LOCKED_WRITTEN: 'đã viết, khoá', OVER_CAP: 'vượt trần số tập tự sửa mỗi lượt', BUDGET: 'đã đạt giới hạn AI', OUTLINE_CHANGED: 'chia tập vừa bị đổi nơi khác',
  STILL_UNVERIFIED: 'viết lại vẫn không xác minh được', AI_ERROR: 'lỗi gọi AI', NO_CLAIMS: 'AI không trả ý', BAD_STRUCTURE: 'AI trả ý sai cấu trúc',
  SOURCE_RANGE_STALE: 'chương nguồn đã đổi', EPISODE_NOT_FOUND: 'không còn tập này', GATE_FAILED: 'cổng không chấm được',
};

/**
 * Ghi chú tự sửa của thẻ gate. Chia tập (P3a) có dữ liệu có cấu trúc `targets`/`unresolved` ⇒ nói đúng tập ("Đã tự sửa tập 3, 9 … còn lỗi ở tập 14 (đã viết, khoá)"); không có ⇒ như cũ (`reason` của localize).
 */
export const autoFixNotes = (a: StepGate['autoFix'] | undefined): string[] => {
  if (!a) return [];
  const out: string[] = [];
  const structured = a.targets !== undefined || a.unresolved !== undefined;
  if (structured) {
    if (a.targets?.length) out.push(`Đã tự sửa tập ${a.targets.join(', ')} (một vòng) rồi chấm lại các tập đó`);
    if (a.unresolved?.length) out.push(`Còn lỗi ở ${a.unresolved.map((u) => `tập ${u.ep} (${AUTOFIX_UNRESOLVED_LABEL[u.reason] ?? u.reason})`).join(', ')}`);
    if (!a.targets?.length && !a.unresolved?.length && a.reason) out.push(`Tự sửa: ${redactMoney(a.reason)}`);
  } else if (a.reason) out.push(`Tự sửa: ${redactMoney(a.reason)}`);
  if (a.manualFix?.length) out.push(`Cần sửa tay (đang khoá): ${a.manualFix.join(', ')}`);
  return out;
};

export interface GateViewContext {
  stepKey: GateStepKey;
  /** Bước đã có kết quả để chấm (chia tập: có tập; localize: đã chạy). Không có kết quả và không có cổng ⇒ không hiện khối. */
  hasResult: boolean;
  /** Tổng chi cộng dồn của truyện (`gateSpentUsd`); null ⇒ dùng `costUsd` của cổng. */
  spentUsd: number | null;
}

const RERUN_HINT: Record<GateStepKey, string> = {
  localize: 'Sửa lại = chạy lại bản địa hoá (bồi chỗ trống) rồi AI chấm lại.',
  episode_split: 'Sửa lại = chạy lại bước chia tập; AI viết lại mọi tập nên có thể sinh lỗi mới ở tập khác.',
};

export const gateView = (gate: StepGate | null | undefined, ctx: GateViewContext): GateView | null => {
  if (!gate && !ctx.hasResult) return null;
  const base = {
    blocking: [] as GateIssueView[], advisory: [] as GateIssueView[], blocksNext: false,
    canRerunStep: false, canRejudge: false, canOverride: false,
    rerunHint: RERUN_HINT[ctx.stepKey], notes: [] as string[],
  };
  if (!gate) {
    return { ...base, state: 'NONE', color: 'default', label: 'Chưa chấm', description: 'Bước này chưa được AI chấm — bấm Chấm lại.', canRejudge: true };
  }

  const blocking = (gate.blockingIssues ?? []).map(toIssueView);
  const advisory = (gate.advisoryIssues ?? []).map(toIssueView);
  const notes: string[] = [];
  if (gate.partial) notes.push(`Chỉ chấm ${gate.windowsJudged ?? '?'}/${gate.windowsTotal ?? '?'} cửa sổ (giới hạn AI) — phần còn lại CHƯA được chấm`);
  notes.push(...autoFixNotes(gate.autoFix));
  // F1: cổng đã bỏ qua vì đạt giới hạn ⇒ câu cố định ở `description`; `note` BE có thể mang «$5/truyện» nên KHÔNG in. Note khác: bỏ SỐ TIỀN.
  if (gate.note && gate.status !== 'SKIPPED_BUDGET') notes.push(redactMoney(gate.note));
  const withBase = { ...base, notes };

  // Cổng chia tập đã cũ vì localize chạy lại ⇒ KHÔNG chặn; mọi lỗi cũ chỉ để tham khảo.
  if (gate.stale) {
    return {
      ...withBase, state: 'STALE', color: 'warning', label: 'Cần chấm lại sau localize',
      description: redactMoney(gate.staleReason) || 'Kết quả chấm này thuộc bản localize cũ — không còn chặn. Chấm lại để cập nhật.',
      advisory: [...blocking, ...advisory], canRejudge: true,
    };
  }

  switch (gate.status) {
    case 'PASSED':
      return {
        ...withBase, state: 'PASSED', color: 'success', label: 'Đã qua cổng AI', blocking, advisory, canRejudge: true,
        description: gate.attempts === 2
          ? 'AI đã tự sửa 1 lần, chấm lại không còn lỗi khách quan.'
          : 'AI không thấy lỗi khách quan (bịa tình tiết / bỏ sót / lộ tên gốc).',
      };
    case 'BLOCKED':
      return {
        ...withBase, state: 'BLOCKED', color: 'error', label: `BỊ CHẶN (${blocking.length} lỗi khách quan)`, blocking, advisory,
        blocksNext: true, canRerunStep: true, canRejudge: true, canOverride: true,
        description: `${gate.attempts === 2 ? 'AI đã tự sửa 1 lần nhưng vẫn còn lỗi. ' : ''}Bước kế tiếp sẽ từ chối cho tới khi sửa lại hoặc bấm "Vẫn tiếp tục" kèm lý do.`,
      };
    case 'OVERRIDDEN':
      return {
        ...withBase, state: 'OVERRIDDEN', color: 'info', label: 'Đã vượt cổng', blocking, advisory, canRejudge: true,
        description: 'Cổng bị bỏ qua có kiểm toán — bước kế tiếp được phép chạy.',
        overrideInfo: gate.override
          ? `${overrideActorText(gate.override)} lúc ${formatVnAuditTime(gate.override.at)} — lý do: "${gate.override.reason}" (${gate.override.issues?.length ?? 0} lỗi bỏ qua)`
          : undefined,
      };
    case 'FAILED':
      return {
        ...withBase, state: 'FAILED', color: 'warning', label: 'Chưa kiểm tra được', advisory: [...blocking, ...advisory], canRejudge: true,
        description: `Giám khảo AI lỗi — KHÔNG chặn. Có thể thử lại.${gate.error ? ` (${redactMoney(gate.error)})` : ''}`,
      };
    case 'SKIPPED_BUDGET':
      return {
        ...withBase, state: 'SKIPPED_BUDGET', color: 'warning', label: 'Đã đạt giới hạn AI',
        description: `Đã đạt giới hạn AI cho các lượt chấm — cổng không gọi AI nữa, duyệt tay.`,
      };
    case 'SKIPPED_OFF':
      return { ...withBase, state: 'SKIPPED_OFF', color: 'default', label: 'Cổng AI đang tắt', description: 'Máy chủ đang tắt cổng AI review (ADAPTATION_REVIEW_GATES=off).' };
    default: {
      // Trạng thái lạ từ server mới hơn FE ⇒ hiện trung tính, không chặn.
      return { ...withBase, state: 'NONE', color: 'default', label: String((gate as { status?: unknown }).status ?? 'Chưa rõ'), description: 'Trạng thái cổng không nhận ra.' };
    }
  }
};
